import { useState, useEffect } from 'react'
import { useApp } from '@/store/AppContext'
import { calculatePrice, formatCurrency } from '@/lib/pricing'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  Banknote,
  CreditCard,
  Smartphone,
  Clock,
  Info,
  CheckCircle2,
} from 'lucide-react'
import { differenceInMinutes } from 'date-fns'
import { useToast } from '@/hooks/use-toast'

interface CheckoutSheetProps {
  ticketId: string
  onClose: () => void
}

export function CheckoutSheet({ ticketId, onClose }: CheckoutSheetProps) {
  const { tickets, rules, checkOut, settings } = useApp()
  const { toast } = useToast()
  const [method, setMethod] = useState<string>('')

  const ticket = tickets.find((t) => t.id === ticketId)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && ticket) {
        if (calculatePrice(ticket, rules) === 0 || method) {
          e.preventDefault()
          handleCheckout()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [method, ticket, rules])

  if (!ticket) return null

  const price = calculatePrice(ticket, rules)
  const durationMins = differenceInMinutes(new Date(), ticket.checkInAt)
  const rule = rules.find((r) => r.type === ticket.type)

  const handleCheckout = () => {
    if (!method && price > 0) {
      toast({
        title: 'Atenção',
        description: 'Selecione uma forma de pagamento.',
        variant: 'destructive',
      })
      return
    }
    checkOut(
      ticket.id,
      price,
      price === 0 ? (ticket.isMonthly ? 'Mensalista' : 'Isento') : method,
    )
    toast({
      title: 'Sucesso',
      description: `Saída de ${ticket.plate} finalizada.`,
    })
    onClose()
  }

  return (
    <Sheet open={true} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg overflow-y-auto flex flex-col p-0"
      >
        <div className="bg-primary text-primary-foreground p-6 pt-12">
          <SheetHeader className="text-left space-y-0">
            <SheetTitle className="text-primary-foreground opacity-80 text-sm font-normal">
              Finalizar Saída
            </SheetTitle>
            <div className="text-4xl font-black tracking-widest mt-2 flex items-center gap-2">
              {ticket.plate}
              {ticket.isMonthly && (
                <span className="bg-white/20 text-xs px-2 py-1 rounded-full font-bold uppercase tracking-normal">
                  Mensalista
                </span>
              )}
            </div>
            <SheetDescription className="text-primary-foreground/70 text-sm mt-1">
              Entrada:{' '}
              {ticket.checkInAt.toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: settings.timezone,
              })}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="p-6 flex-1 space-y-8">
          <div className="flex items-center justify-between border-b pb-6">
            <div className="flex items-center gap-3">
              <div className="bg-muted p-3 rounded-full">
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Permanência</div>
                <div className="font-bold text-xl">
                  {Math.floor(durationMins / 60)}h {durationMins % 60}m
                </div>
              </div>
            </div>
          </div>

          <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-6 text-center space-y-2">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Valor a cobrar
            </div>
            <div className="text-5xl font-black text-secondary">
              {formatCurrency(price)}
            </div>
            {rule && !ticket.isMonthly && (
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-2 opacity-80">
                <Info className="h-3 w-3" /> Regra: {ticket.type} (
                {formatCurrency(rule.firstHour)}/1ªh +{' '}
                {formatCurrency(rule.additionalHour)}/h adic.)
              </div>
            )}
          </div>

          {price > 0 ? (
            <div className="space-y-4">
              <h3 className="font-medium text-muted-foreground">
                Forma de Pagamento
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  type="button"
                  variant={method === 'Dinheiro' ? 'default' : 'outline'}
                  className={`h-24 flex flex-col gap-2 ${method === 'Dinheiro' ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  onClick={() => setMethod('Dinheiro')}
                >
                  <Banknote className="h-8 w-8 text-emerald-600" />
                  <span>Dinheiro</span>
                </Button>
                <Button
                  type="button"
                  variant={method === 'Pix' ? 'default' : 'outline'}
                  className={`h-24 flex flex-col gap-2 ${method === 'Pix' ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  onClick={() => setMethod('Pix')}
                >
                  <Smartphone className="h-8 w-8 text-teal-600" />
                  <span>Pix</span>
                </Button>
                <Button
                  type="button"
                  variant={method === 'Cartão' ? 'default' : 'outline'}
                  className={`h-24 flex flex-col gap-2 ${method === 'Cartão' ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  onClick={() => setMethod('Cartão')}
                >
                  <CreditCard className="h-8 w-8 text-blue-600" />
                  <span>Cartão</span>
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 text-emerald-800 p-4 rounded-lg border border-emerald-200 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6" />
              <div>
                {ticket.isMonthly
                  ? 'Veículo Mensalista. Nenhuma cobrança avulsa necessária.'
                  : 'Veículo dentro do tempo de tolerância ou isento. Nenhuma cobrança necessária.'}
              </div>
            </div>
          )}
        </div>

        <SheetFooter className="p-6 bg-background border-t mt-auto">
          <Button
            onClick={handleCheckout}
            className="w-full h-16 text-xl font-bold shadow-elevation"
            disabled={price > 0 && !method}
          >
            Confirmar e Finalizar
            {(method || price === 0) && (
              <kbd className="hidden md:inline-block ml-3 text-xs font-normal border border-primary-foreground/30 bg-primary-foreground/10 rounded px-2 py-1">
                Enter
              </kbd>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
